import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Upload,
  BookOpen,
  FileText,
  Image,
  Table,
  CheckCircle,
  Clock,
  AlertCircle,
  Trash2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Filter,
  X,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { knowledgeAPI } from "../api";
import { useAuthStore } from "../store/authStore";
import DocumentUpload from "./DocumentUpload";
import toast from "react-hot-toast";
import clsx from "clsx";

// Document type configuration with icons and styling
const typeConfig = {
  manual: { label: "Manual", icon: BookOpen, color: "text-blue-600", bg: "bg-blue-50" },
  sop: { label: "SOP", icon: FileText, color: "text-emerald-600", bg: "bg-emerald-50" },
  guide: { label: "Guide", icon: BookOpen, color: "text-cyan-600", bg: "bg-cyan-50" },
  troubleshooting: {
    label: "Troubleshooting",
    icon: AlertCircle,
    color: "text-amber-600",
    bg: "bg-amber-50",
  },
  specification: {
    label: "Specification",
    icon: Table,
    color: "text-purple-600",
    bg: "bg-purple-50",
  },
  diagram: { label: "Diagram", icon: Image, color: "text-pink-600", bg: "bg-pink-50" },
  safety: { label: "Safety", icon: AlertCircle, color: "text-rose-600", bg: "bg-rose-50" },
  other: { label: "Other", icon: FileText, color: "text-slate-600", bg: "bg-slate-50" },
};

// Status configuration matching SOPs & Dashboard design system
const statusConfig = {
  approved: {
    label: "Approved",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200/80",
    dot: "bg-emerald-500",
    icon: CheckCircle,
  },
  pending_review: {
    label: "Pending Review",
    badge: "bg-amber-50 text-amber-700 border-amber-200/80",
    dot: "bg-amber-500",
    icon: Clock,
  },
  processing: {
    label: "Processing",
    badge: "bg-blue-50 text-blue-700 border-blue-200/80",
    dot: "bg-blue-500",
    icon: Clock,
  },
  rejected: {
    label: "Rejected",
    badge: "bg-rose-50 text-rose-700 border-rose-200/80",
    dot: "bg-rose-500",
    icon: AlertCircle,
  },
  archived: {
    label: "Archived",
    badge: "bg-slate-100 text-slate-700 border-slate-200",
    dot: "bg-slate-400",
    icon: FileText,
  },
};

/**
 * Renders a single Knowledge Document row matching SOPs.jsx 12-column layout
 */
const DocumentListItem = ({ doc, index, isAdmin, onDelete }) => {
  const type = typeConfig[doc.documentType] || typeConfig.other;
  const status = statusConfig[doc.status] || statusConfig.processing;
  const TypeIcon = type.icon;

  const confidence = doc.extraction?.confidenceScore;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.03, 0.3) }}
    >
      <Link
        to={`/knowledge/${doc._id}`}
        className="grid grid-cols-12 items-center px-4 py-3.5 text-sm border-b border-slate-100/80 transition-colors hover:bg-blue-50/30 group cursor-pointer last:border-b-0"
      >
        {/* Column 1: Title & Type (span 4) */}
        <div className="col-span-4 flex items-center gap-3 min-w-0 pr-4">
          <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center flex-shrink-0 group-hover:scale-105 group-hover:bg-blue-600 group-hover:text-white transition-all duration-200">
            <TypeIcon className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors truncate text-sm">
              {doc.title}
            </p>
            <p className="text-xs text-slate-500 mt-0.5 truncate font-medium">
              {type.label}
            </p>
          </div>
        </div>

        {/* Column 2: Status Badge (span 2) */}
        <div className="col-span-2 flex items-center">
          <span
            className={clsx(
              "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border",
              status.badge
            )}
          >
            <span className={clsx("w-1.5 h-1.5 rounded-full", status.dot)} />
            <span>{status.label}</span>
          </span>
        </div>

        {/* Column 3: Category (span 2) */}
        <div className="col-span-2 flex items-center">
          <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium text-slate-700 bg-slate-100/90 capitalize border border-slate-200/50 truncate max-w-[150px]">
            {doc.category || "General"}
          </span>
        </div>

        {/* Column 4: Confidence Score (span 2) */}
        <div className="col-span-2 flex items-center">
          {confidence !== undefined ? (
            <span
              className={clsx(
                "inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold border",
                confidence >= 0.8
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200/70"
                  : confidence >= 0.6
                    ? "bg-amber-50 text-amber-700 border-amber-200/70"
                    : "bg-rose-50 text-rose-700 border-rose-200/70"
              )}
            >
              <span>{Math.round(confidence * 100)}%</span>
            </span>
          ) : (
            <span className="text-xs text-slate-400 font-medium">—</span>
          )}
        </div>

        {/* Column 5: Created Date (span 2) */}
        <div className="col-span-2 text-xs font-medium text-slate-500 whitespace-nowrap">
          {new Date(doc.createdAt).toLocaleDateString()}
        </div>
      </Link>
    </motion.div>
  );
};

const Knowledge = () => {
  const PAGE_SIZE = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isTypeOpen, setIsTypeOpen] = useState(false);
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [deletingDoc, setDeletingDoc] = useState(null);

  const typeRef = useRef(null);
  const statusRef = useRef(null);

  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === "super_admin" || user?.role === "admin";

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (typeRef.current && !typeRef.current.contains(event.target))
        setIsTypeOpen(false);
      if (statusRef.current && !statusRef.current.contains(event.target))
        setIsStatusOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const { data: documentsData, isLoading } = useQuery({
    queryKey: ["knowledge", { type: typeFilter, status: statusFilter, page: currentPage }],
    queryFn: () =>
      knowledgeAPI
        .getAll({
          type: typeFilter || undefined,
          status: statusFilter || undefined,
          page: currentPage,
          limit: PAGE_SIZE,
        })
        .then((res) => res.data),
  });

  const { data: searchResults, isLoading: isSearching } = useQuery({
    queryKey: ["knowledge-search", search, currentPage],
    queryFn: () =>
      knowledgeAPI.search({ q: search, page: currentPage, limit: PAGE_SIZE }).then((res) => res.data),
    enabled: search.length > 2,
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => knowledgeAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["knowledge"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-search"] });
      toast.success("Document deleted successfully");
      setDeletingDoc(null);
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || "Failed to delete document");
    },
  });

  const handleDeleteConfirm = () => {
    if (deletingDoc) {
      deleteMutation.mutate(deletingDoc._id);
    }
  };

  const activeResponse = search.length > 2 ? searchResults : documentsData;
  const rawDocuments = Array.isArray(activeResponse?.data)
    ? activeResponse.data
    : Array.isArray(activeResponse)
      ? activeResponse
      : [];

  const paginationInfo = activeResponse?.pagination;
  const totalItems = paginationInfo?.total ?? rawDocuments.length;
  const totalPages = paginationInfo?.pages || Math.ceil(totalItems / PAGE_SIZE) || 1;

  // Resilient slice in case backend returns all unpaginated items
  const displayedDocuments =
    rawDocuments.length > PAGE_SIZE
      ? rawDocuments.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
      : rawDocuments;

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (currentPage <= 4) {
      return [1, 2, 3, 4, 5, "...", totalPages];
    }
    if (currentPage >= totalPages - 3) {
      return [
        1,
        "...",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }
    return [
      1,
      "...",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "...",
      totalPages,
    ];
  };

  const activeTypeObj = typeFilter ? typeConfig[typeFilter] : null;
  const ActiveTypeIcon = activeTypeObj ? activeTypeObj.icon : null;

  return (
    <div className="space-y-5 lg:space-y-6">
      {/* Top Header matching SOPs & Dashboard */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
            Knowledge Base
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Machine manuals, technical specifications, and diagnostic documentation
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => setIsUploadModalOpen(true)}
            className="btn btn-primary text-xs sm:text-sm py-2 px-3.5 sm:px-4 shadow-md shadow-blue-500/20 whitespace-nowrap inline-flex items-center gap-2 rounded-xl"
          >
            <Upload className="w-4 h-4" />
            <span>Upload Document</span>
          </button>
        </div>
      </div>

      {/* Search & Control Filter Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        {/* Search Input */}
        <div className="relative flex-1 sm:max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            placeholder="Search documents by title or keyword..."
            className="w-full pl-9 pr-9 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 shadow-xs transition-all"
          />
          {isSearching && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
            </div>
          )}
          {!isSearching && search && (
            <button
              onClick={() => {
                setSearch("");
                setCurrentPage(1);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Filter Dropdowns */}
        <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
          {/* Status Filter Dropdown */}
          <div ref={statusRef} className="relative w-full sm:w-auto">
            <button
              type="button"
              onClick={() => setIsStatusOpen((prev) => !prev)}
              className="btn btn-secondary w-full sm:w-auto justify-between h-9 text-xs sm:text-sm px-3.5 bg-white shadow-xs hover:border-slate-400 rounded-xl"
            >
              <div className="flex items-center gap-2">
                <Filter className="w-3.5 h-3.5 text-slate-500" />
                {statusFilter && statusConfig[statusFilter] ? (
                  <div className="flex items-center gap-1.5">
                    <span
                      className={clsx(
                        "w-2 h-2 rounded-full",
                        statusConfig[statusFilter].dot
                      )}
                    />
                    <span className="font-semibold text-slate-700">
                      {statusConfig[statusFilter].label}
                    </span>
                  </div>
                ) : (
                  <span className="font-semibold text-slate-700">All Status</span>
                )}
              </div>
              <ChevronDown
                className={clsx(
                  "w-3.5 h-3.5 text-slate-500 transition-transform duration-200",
                  isStatusOpen && "rotate-180"
                )}
              />
            </button>

            <AnimatePresence>
              {isStatusOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.98 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 mt-1.5 w-full sm:w-52 origin-top-right rounded-xl bg-white shadow-lg border border-slate-200/90 py-1.5 z-20"
                >
                  <button
                    type="button"
                    onClick={() => {
                      setStatusFilter("");
                      setCurrentPage(1);
                      setIsStatusOpen(false);
                    }}
                    className={clsx(
                      "flex items-center justify-between w-full px-3.5 py-2 text-xs text-left transition-colors font-medium",
                      statusFilter === ""
                        ? "bg-blue-50 text-blue-700 font-semibold"
                        : "text-slate-700 hover:bg-slate-50"
                    )}
                  >
                    <span>All Status</span>
                    {statusFilter === "" && (
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                    )}
                  </button>
                  {Object.entries(statusConfig).map(([key, config]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        setStatusFilter(key);
                        setCurrentPage(1);
                        setIsStatusOpen(false);
                      }}
                      className={clsx(
                        "flex items-center justify-between w-full px-3.5 py-2 text-xs text-left transition-colors font-medium",
                        statusFilter === key
                          ? "bg-blue-50 text-blue-700 font-semibold"
                          : "text-slate-700 hover:bg-slate-50"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <span className={clsx("w-2 h-2 rounded-full", config.dot)} />
                        <span>{config.label}</span>
                      </div>
                      {statusFilter === key && (
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                      )}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Type Filter Dropdown */}
          <div ref={typeRef} className="relative w-full sm:w-auto">
            <button
              type="button"
              onClick={() => setIsTypeOpen((prev) => !prev)}
              className="btn btn-secondary w-full sm:w-auto justify-between h-9 text-xs sm:text-sm px-3.5 bg-white shadow-xs hover:border-slate-400 rounded-xl"
            >
              <div className="flex items-center gap-2 truncate max-w-[140px] sm:max-w-none">
                {ActiveTypeIcon && (
                  <ActiveTypeIcon
                    className={clsx("w-3.5 h-3.5", activeTypeObj?.color)}
                  />
                )}
                <span className="font-semibold text-slate-700 truncate">
                  {activeTypeObj?.label || "All Types"}
                </span>
              </div>
              <ChevronDown
                className={clsx(
                  "w-3.5 h-3.5 text-slate-500 transition-transform duration-200",
                  isTypeOpen && "rotate-180"
                )}
              />
            </button>

            <AnimatePresence>
              {isTypeOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.98 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 mt-1.5 w-full sm:w-56 origin-top-right rounded-xl bg-white shadow-lg border border-slate-200/90 py-1.5 z-20 max-h-64 overflow-y-auto"
                >
                  <button
                    type="button"
                    onClick={() => {
                      setTypeFilter("");
                      setCurrentPage(1);
                      setIsTypeOpen(false);
                    }}
                    className={clsx(
                      "flex items-center justify-between w-full px-3.5 py-2 text-xs text-left transition-colors font-medium",
                      typeFilter === ""
                        ? "bg-blue-50 text-blue-700 font-semibold"
                        : "text-slate-700 hover:bg-slate-50"
                    )}
                  >
                    <span>All Types</span>
                    {typeFilter === "" && (
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                    )}
                  </button>
                  {Object.keys(typeConfig).map((type) => {
                    const ConfigIcon = typeConfig[type].icon;
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => {
                          setTypeFilter(type);
                          setCurrentPage(1);
                          setIsTypeOpen(false);
                        }}
                        className={clsx(
                          "flex items-center justify-between w-full px-3.5 py-2 text-xs text-left transition-colors font-medium",
                          typeFilter === type
                            ? "bg-blue-50 text-blue-700 font-semibold"
                            : "text-slate-700 hover:bg-slate-50"
                        )}
                      >
                        <div className="flex items-center gap-2 truncate">
                          <ConfigIcon
                            className={clsx("w-3.5 h-3.5", typeConfig[type].color)}
                          />
                          <span>{typeConfig[type].label}</span>
                        </div>
                        {typeFilter === type && (
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-600 flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Reset Filters Shortcut */}
          {(search || statusFilter || typeFilter) && (
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setStatusFilter("");
                setTypeFilter("");
                setCurrentPage(1);
              }}
              className="text-xs font-semibold text-slate-500 hover:text-rose-600 px-2.5 py-1.5 rounded-xl hover:bg-rose-50 transition-colors"
              title="Reset all filters"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Document Listing View */}
      {isLoading ? (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
          <div className="divide-y divide-slate-100">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="grid grid-cols-12 items-center p-4 border-b border-slate-100 last:border-b-0 animate-pulse"
              >
                {/* Title & Type (4) */}
                <div className="col-span-4 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-slate-200 flex-shrink-0" />
                  <div className="space-y-1.5 flex-1">
                    <div className="h-4 w-44 bg-slate-200 rounded" />
                    <div className="h-3 w-20 bg-slate-100 rounded" />
                  </div>
                </div>
                {/* Status (2) */}
                <div className="col-span-2">
                  <div className="h-6 w-24 bg-slate-200 rounded-full" />
                </div>
                {/* Category (2) */}
                <div className="col-span-2">
                  <div className="h-5 w-20 bg-slate-100 rounded-lg" />
                </div>
                {/* Confidence (2) */}
                <div className="col-span-2">
                  <div className="h-5 w-14 bg-slate-100 rounded-full" />
                </div>
                {/* Created (2) */}
                <div className="col-span-2">
                  <div className="h-4 w-16 bg-slate-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : displayedDocuments && displayedDocuments.length > 0 ? (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
            <div className="overflow-x-auto">
              <div className="min-w-[760px]">
                {/* Table Header (4-2-2-2-2 = 12 cols) */}
                <div className="grid grid-cols-12 items-center text-[11px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50/80 border-b border-slate-200/80 px-4 py-3">
                  <div className="col-span-4">Document / Type</div>
                  <div className="col-span-2">Status</div>
                  <div className="col-span-2">Category</div>
                  <div className="col-span-2">Confidence</div>
                  <div className="col-span-2">Created</div>
                </div>

                {/* List Body */}
                <div className="divide-y divide-slate-100/90">
                  <AnimatePresence>
                    {displayedDocuments.map((doc, index) => (
                      <DocumentListItem
                        key={doc._id}
                        doc={doc}
                        index={index}
                        isAdmin={isAdmin}
                        onDelete={setDeletingDoc}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>

          {/* Pagination Controls - only shown after 10 items (totalItems > PAGE_SIZE) */}
          {!isLoading && totalItems > PAGE_SIZE && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-slate-200/80">
              <p className="text-xs text-slate-500 font-medium">
                Showing{" "}
                <span className="font-semibold text-slate-700">
                  {(currentPage - 1) * PAGE_SIZE + 1}
                </span>{" "}
                to{" "}
                <span className="font-semibold text-slate-700">
                  {Math.min(currentPage * PAGE_SIZE, totalItems)}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-slate-700">{totalItems}</span>{" "}
                documents
              </p>

              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  className={clsx(
                    "h-8 px-2.5 rounded-xl border text-xs font-medium flex items-center gap-1 transition-colors",
                    currentPage <= 1
                      ? "border-slate-200 text-slate-300 cursor-not-allowed bg-slate-50"
                      : "border-slate-200 text-slate-700 bg-white hover:bg-slate-50 active:bg-slate-100 shadow-xs"
                  )}
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Prev</span>
                </button>

                <div className="flex items-center gap-1">
                  {getPageNumbers().map((pageItem, idx) =>
                    pageItem === "..." ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="w-8 h-8 flex items-center justify-center text-xs text-slate-400 font-bold"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={pageItem}
                        type="button"
                        onClick={() => setCurrentPage(pageItem)}
                        className={clsx(
                          "w-8 h-8 rounded-xl text-xs font-semibold transition-all",
                          currentPage === pageItem
                            ? "bg-blue-600 text-white shadow-sm shadow-blue-500/30 font-bold"
                            : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 shadow-xs"
                        )}
                      >
                        {pageItem}
                      </button>
                    )
                  )}
                </div>

                <button
                  type="button"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  className={clsx(
                    "h-8 px-2.5 rounded-xl border text-xs font-medium flex items-center gap-1 transition-colors",
                    currentPage >= totalPages
                      ? "border-slate-200 text-slate-300 cursor-not-allowed bg-slate-50"
                      : "border-slate-200 text-slate-700 bg-white hover:bg-slate-50 active:bg-slate-100 shadow-xs"
                  )}
                >
                  <span className="hidden sm:inline">Next</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Empty State */
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-10 sm:p-14 text-center">
          <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-500 mx-auto mb-4 flex items-center justify-center">
            <BookOpen className="w-8 h-8" />
          </div>
          <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1.5">
            No Documents Found
          </h3>
          <p className="text-xs sm:text-sm text-slate-500 max-w-sm mx-auto mb-5">
            {search || statusFilter || typeFilter
              ? "No technical documents match your current search and filter criteria."
              : "No documentation uploaded yet. Get started by uploading your first manual or guide."}
          </p>
          <div className="flex items-center justify-center gap-3">
            {(search || statusFilter || typeFilter) && (
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setStatusFilter("");
                  setTypeFilter("");
                  setCurrentPage(1);
                }}
                className="btn btn-secondary text-xs sm:text-sm py-2 px-3.5 rounded-xl"
              >
                Clear Filters
              </button>
            )}
            <button
              type="button"
              onClick={() => setIsUploadModalOpen(true)}
              className="btn btn-primary text-xs sm:text-sm py-2 px-4 shadow-md shadow-blue-500/20 inline-flex items-center gap-2 rounded-xl"
            >
              <Upload className="w-4 h-4" />
              <span>Upload Document</span>
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal matching SOPs.jsx */}
      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {deletingDoc && (
              <div
                className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-slate-950/70 backdrop-blur-sm overflow-y-auto"
                onClick={() => {
                  if (!deleteMutation.isPending) setDeletingDoc(null);
                }}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="relative w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-2xl p-6 space-y-4 my-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Header */}
                  <div className="flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 border border-rose-100/80 flex items-center justify-center shrink-0">
                      <Trash2 className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight">
                        Delete Document
                      </h2>
                    </div>
                  </div>

                  {/* Warning Description */}
                  <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">
                    Are you sure you want to delete{" "}
                    <span className="font-semibold text-slate-800">
                      {deletingDoc.title}
                    </span>
                    ? This action cannot be undone. Any AI search embeddings and maintenance links referencing this document will be removed.
                  </p>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-end gap-3 pt-2">
                    <button
                      type="button"
                      disabled={deleteMutation.isPending}
                      onClick={() => setDeletingDoc(null)}
                      className="btn btn-secondary text-xs sm:text-sm py-2 px-4 hover:border-slate-300"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={deleteMutation.isPending}
                      onClick={handleDeleteConfirm}
                      className="btn bg-rose-600 hover:bg-rose-700 text-white text-xs sm:text-sm py-2 px-4 shadow-md shadow-rose-500/20 inline-flex items-center gap-2 font-medium"
                    >
                      {deleteMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Deleting...</span>
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4" />
                          <span>Delete Document</span>
                        </>
                      )}
                    </button>
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>,
          document.body
        )}

      {/* Upload Document Popup Modal */}
      <DocumentUpload
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
      />
    </div>
  );
};

export default Knowledge;
